import commandLineArgs from "command-line-args";
import commandLineUsage from "command-line-usage";

export function optionsDefinition(...defs) {
    return defs;
}

export function commandDefinition(name, type, options, commandOptions) {
    return {
        name: name,
        typeValue: type,
        options: options,
        ...commandOptions,
    };
}

export function commandsDefinition(...defs) {
    return defs;
}

export class InvalidCommandLineArgumentsError extends Error {
    constructor(message, usageText) {
        super(message);
        this.usageText = usageText;
    }
}

export class CommandLineArgumentsHelpRequestedError extends Error {
    constructor(usageText) {
        super("");
        this.usageText = usageText;
    }
}

export function buildCommandBasedOptionsParser(commandDefinitions, options) {
    function parseCommandLineArgs(args) {
        const options = commandLineArgs([{ name: "command", defaultOption: true, defaultValue: "help" }], {
            argv: args,
            stopAtFirstUnknown: true,
        });
        if (options.command === "help") {
            throw new CommandLineArgumentsHelpRequestedError(getCommandListUsage());
        }
        const commandDefinition = commandDefinitions.find(x => x.name === options.command);
        if (commandDefinition == undefined) {
            throw new InvalidCommandLineArgumentsError(
                `Команда не найдена: ${options.command}.`,
                getCommandListUsage()
            );
        }

        try {
            const commandArgs = options._unknown || [];
            let patchedOptionDefinitions = [
                ...commandDefinition.options.map(toOptionDefinition),
                {
                    name: "help",
                    alias: "h",
                    type: Boolean,
                },
            ];
            const parsedCommandOptions = commandLineArgs(patchedOptionDefinitions, {
                argv: commandArgs,
            });
            if (parsedCommandOptions["help"]) {
                throw new CommandLineArgumentsHelpRequestedError(getCommandUsage(commandDefinition.name));
            }
            const result = {
                type: commandDefinition.typeValue,
                options: {},
            };
            for (const optionDefinition of commandDefinition.options) {
                let newVar;
                if (optionDefinition.multiple) {
                    newVar =
                        parsedCommandOptions[optionDefinition.name][0] === defaultValueMarker
                            ? optionDefinition.defaultValue
                            : optionDefinition.parse(parsedCommandOptions[optionDefinition.name]);
                } else {
                    newVar =
                        parsedCommandOptions[optionDefinition.name] === defaultValueMarker
                            ? optionDefinition.defaultValue
                            : optionDefinition.parse(parsedCommandOptions[optionDefinition.name]);
                }
                result.options = {
                    ...result.options,
                    ...newVar,
                };
            }
            return result;
        } catch (e) {
            if (e instanceof CommandLineArgumentsHelpRequestedError) {
                throw e;
            }
            const message = e instanceof Error ? e.message : e;
            throw new InvalidCommandLineArgumentsError(message, getCommandUsage(commandDefinition.name));
        }
    }

    function getCommandListUsage() {
        return commandLineUsage([
            {
                header: options.commandName,
                content: options.commandDescription,
            },
            {
                header: "Использование",
                content: options.usageString || `${options.commandName} [команда] [опции...]`,
            },
            {
                header: "Список команд",
                content: [
                    ...commandDefinitions.map(x => ({
                        name: x.name,
                        summary: x.description,
                    })),
                    { name: "help", summary: "Выводит этот список команд" },
                ],
            },
            {
                header: "Справка по команде",
                content: `${options.commandName} [команда] --help`,
            },
        ]);
    }

    function getCommandUsage(name) {
        const commandDefinition = commandDefinitions.find(x => x.name === name);
        return commandLineUsage([
            {
                header: `${options.commandName} ${commandDefinition.name} [опции...]`,
                content: options.description,
            },
            {
                header: "Список опций",
                optionList: [
                    ...commandDefinition.options,
                    {
                        name: "help",
                        alias: "h",
                        description: "Выводит справку по данной команде.",
                        type: Boolean,
                    },
                ],
            },
        ]);
    }

    parseCommandLineArgs.getCommandListUsage = getCommandListUsage;
    parseCommandLineArgs.getCommandUsage = getCommandUsage;

    return parseCommandLineArgs;
}

const defaultValueMarker = { __DEFAULT__: true };

function toOptionDefinition(x) {
    let result = {
        name: x.name,
        defaultValue: defaultValueMarker,
        multiple: x.multiple,
        defaultOption: x.defaultOption,
        alias: x.alias,
    };
    if (x.doesNotRequireValue) {
        result.type = Boolean;
    }
    return result;
}
